import { apiErrorMessage } from '@agos/api-client'
import { T, tt } from '@agos/shared'
import { BUTTON_PRIMARY, cn, ConfirmDialog, IconButton, Spinner, useToast } from '@agos/ui'
import { AlertTriangle, Network, X } from 'lucide-react'
import { useState } from 'react'

import {
  useReplaceDelegations,
  useRolesOverview,
  type RoleDelegationEdge,
  type RoleView,
} from '../../roles/roles-api'
import {
  delegationDiff,
  delegationIsDirty,
  delegationRules,
  edgesOf,
  gainers,
  graphProblems,
  passedOnText,
  problemText,
  rightsPassedOn,
  starterApplied,
  targetProblem,
  withoutManager,
  withStarter,
  withVerb,
  type Verb,
} from '../delegation'
import { orderRoles, roleNameOf } from '../role-model'
import { RoleChip } from '../RoleChip'
import { TabReadFailed } from '../TabReadFailed'

/**
 * *Kto spravuje role* - who may hand which role down, as sentences. The main administrator's tab (design §2.9), on
 * `PUT /settings/roles/delegations` (§10A.408).
 *
 * **The four rules are always on screen**, because they are what makes a delegation safe and a reader drawing one
 * should not have to remember them; a target the rules refuse is greyed in the picker **with its reason**; and an
 * assign edge whose target holds rights its manager does not says so, because handing out such a role is handing those
 * rights on (the server allows it - the main administrator drew the edge knowing - and the screen makes sure they do).
 *
 * The empty state is drawn only after a successful read: *nobody manages anything* said over a failed read would be a
 * claim about the franchise the screen cannot make (§9.543). The one-click starter is owner question #166's answer -
 * offered, never seeded - and nothing changes until **Uložiť**, whose confirmation names the people who gain the power.
 */
export function DelegationTab() {
  const { toast } = useToast()
  const overview = useRolesOverview()
  const saveGraph = useReplaceDelegations()
  const [draft, setDraft] = useState<RoleDelegationEdge[] | null>(null)
  const [extraManagers, setExtraManagers] = useState<string[]>([])
  const [confirming, setConfirming] = useState(false)

  if (overview.isError) {
    return (
      <TabReadFailed what="delegation" retry={() => void overview.refetch()}>
        <T>Bez načítaných rolí by sa spravovanie uložilo celé nanovo z prázdneho zoznamu. Ukladanie je preto vypnuté.</T>
      </TabReadFailed>
    )
  }

  if (overview.isLoading || overview.data === undefined) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  const data = overview.data
  const roles = orderRoles(data.roles)
  const stored = edgesOf(data.roles)
  const edges = draft ?? stored
  const diff = delegationDiff(stored, edges)
  const dirty = draft !== null && delegationIsDirty(diff)
  const problems = graphProblems(edges, data.roles)
  const managers = roles.filter(
    (role) =>
      role.key !== 'main_admin' &&
      (edges.some((edge) => edge.manager === role.key) || extraManagers.includes(role.key)),
  )
  const addable = roles.filter((role) => role.key !== 'main_admin' && !managers.includes(role))
  const change = (next: RoleDelegationEdge[]) => setDraft(next)
  const gain = gainers(diff, data.roles, data.people)

  const confirmText =
    gain.length === 0
      ? tt('Nikto nedostane novú moc - spravovanie sa len zužuje.')
      : `${tt('Novú moc nad rolami dostanú ľudia s rolou')} ${gain
          .map(
            (one) =>
              `${one.name} (${one.people.length === 0 ? tt('zatiaľ nikto') : one.people.map((person) => person.name).join(', ')})`,
          )
          .join('; ')}.`

  const save = () =>
    saveGraph.mutate(
      { data: { edges } },
      {
        onSuccess: () => {
          setConfirming(false)
          setDraft(null)
          setExtraManagers([])
          toast({ title: tt('Spravovanie rolí uložené') })
        },
        onError: (error: unknown) => {
          setConfirming(false)
          toast({
            title: tt('Chyba'),
            description: apiErrorMessage(error, tt('Spravovanie rolí sa neuložilo.')),
            variant: 'destructive',
          })
        },
      },
    )

  return (
    <div className="space-y-4" data-delegation-tab>
      <div className="rounded-2xl border border-border bg-card p-4 space-y-1.5" data-delegation-rules>
        <p className="text-sm font-bold">{tt('Pravidlá spravovania')}</p>
        <ul className="list-disc pl-5 space-y-0.5">
          {delegationRules().map((rule) => (
            <li key={rule} className="text-xs text-muted-foreground">
              {rule}
            </li>
          ))}
        </ul>
      </div>

      {stored.length === 0 && draft === null && (
        <p className="text-xs text-muted-foreground" data-delegation-empty>
          {tt('Zatiaľ nikto nespravuje žiadnu rolu - práva rolí a role ľudí mení len hlavný administrátor.')}
        </p>
      )}

      {!starterApplied(edges, data.roles) && (
        <button
          type="button"
          onClick={() => change(withStarter(edges, data.roles))}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-primary/40 text-primary text-xs font-semibold hover:bg-primary/10"
          data-delegation-starter
        >
          <Network className="w-4 h-4" />
          {tt('Administrátori spravujú Kuchára, Čašníka a Šoféra')}
        </button>
      )}

      <div className="space-y-3">
        {managers.map((manager) => (
          <ManagerRow
            key={manager.key}
            manager={manager}
            edges={edges}
            roles={roles}
            catalogue={data.allPermissions}
            problems={problems}
            onChange={change}
            onRemove={() => {
              change(withoutManager(edges, manager.key))
              setExtraManagers((current) => current.filter((key) => key !== manager.key))
            }}
          />
        ))}
      </div>

      {addable.length > 0 && (
        <label className="flex flex-wrap items-center gap-2 text-xs" data-delegation-add-manager>
          <span className="text-muted-foreground">{tt('Pridať rolu, ktorá spravuje iné')}:</span>
          <select
            value=""
            onChange={(event) => {
              const key = event.target.value
              if (key !== '') setExtraManagers((current) => [...current, key])
            }}
            className="px-3 py-1.5 rounded-xl bg-background border border-border text-xs"
          >
            <option value="">{tt('Vyberte rolu')}</option>
            {addable.map((role) => (
              <option key={role.key} value={role.key}>
                {roleNameOf(data.roles, role.key)}
              </option>
            ))}
          </select>
        </label>
      )}

      {dirty && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-xl border-2 border-primary/40 bg-primary/5 p-3"
          data-delegation-dirty
        >
          <p className="text-xs font-bold flex-1">
            {diff.added.length + diff.changed.length + diff.removed.length} {tt('zmien v spravovaní')}
          </p>
          {problems.length > 0 && (
            <p className="w-full text-xs text-red-500" data-delegation-blocked>
              {tt('Server by spravovanie odmietol - opravte väzby označené vyššie.')}
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              setDraft(null)
              setExtraManagers([])
            }}
            className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
          >
            <T>Zahodiť</T>
          </button>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={saveGraph.isPending || problems.length > 0}
            className={cn('px-3 py-1.5 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
            data-delegation-save
          >
            <T>Uložiť</T>
          </button>
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={save}
        title={tt('Uložiť spravovanie rolí?')}
        description={confirmText}
        confirmLabel={tt('Uložiť')}
        loading={saveGraph.isPending}
      />
    </div>
  )
}

/**
 * One manager role as a sentence - *Ľudia s rolou [Vedúci smeny] môžu upravovať práva rolí: [Čašník ×][+] · prideľovať
 * ľuďom: [Čašník ×][Kuchár ×][+]*.
 */
function ManagerRow({
  manager,
  edges,
  roles,
  catalogue,
  problems,
  onChange,
  onRemove,
}: {
  manager: RoleView
  edges: RoleDelegationEdge[]
  roles: RoleView[]
  catalogue: readonly string[]
  problems: { edge: RoleDelegationEdge; problem: ReturnType<typeof targetProblem> }[]
  onChange: (next: RoleDelegationEdge[]) => void
  onRemove: () => void
}) {
  const mine = edges.filter((edge) => edge.manager === manager.key)
  const nameOf = (key: string) => roleNameOf(roles, key)
  const verbs: { verb: Verb; label: string }[] = [
    { verb: 'edit', label: tt('môžu upravovať práva rolí') },
    { verb: 'assign', label: tt('prideľovať ľuďom') },
  ]
  const passedOn = mine
    .filter((edge) => edge.assign)
    .map((edge) => ({
      target: edge.target,
      rights: rightsPassedOn(manager, roles.find((role) => role.key === edge.target), catalogue),
    }))
    .filter((one) => one.rights.length > 0)
  const ownProblems = problems.filter((one) => one.edge.manager === manager.key)

  return (
    <div className="rounded-2xl border border-border bg-card p-4 space-y-2" data-delegation-manager={manager.key}>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span>{tt('Ľudia s rolou')}</span>
        <RoleChip roleKey={manager.key} roles={roles} />
        <span className="ml-auto">
          <IconButton
            label={tt('Odobrať spravovanie tejto roly')}
            tone="red"
            onClick={onRemove}
            attributes={{ 'data-delegation-remove-manager': manager.key }}
          >
            <X className="w-3.5 h-3.5" />
          </IconButton>
        </span>
      </div>

      {verbs.map(({ verb, label }) => {
        const targets = mine.filter((edge) => edge[verb]).map((edge) => edge.target)
        return (
          <div key={verb} className="flex flex-wrap items-center gap-1.5 text-xs" data-delegation-verb={verb}>
            <span className="text-muted-foreground">{label}:</span>
            {targets.length === 0 && <span className="text-muted-foreground">{tt('žiadne')}</span>}
            {targets.map((target) => (
              <span
                key={target}
                className="flex items-center gap-1 rounded-full border border-border pl-1 pr-1.5 py-0.5"
                data-delegation-edge={`${manager.key}>${target}:${verb}`}
              >
                <RoleChip roleKey={target} roles={roles} />
                <button
                  type="button"
                  onClick={() => onChange(withVerb(edges, manager.key, target, verb, false))}
                  aria-label={`${tt('Odobrať')} ${nameOf(target)}`}
                  className="text-muted-foreground hover:text-red-500"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <select
              value=""
              onChange={(event) => {
                const target = event.target.value
                if (target !== '') onChange(withVerb(edges, manager.key, target, verb, true))
              }}
              aria-label={`${label} - ${tt('pridať rolu')}`}
              className="px-2 py-1 rounded-lg bg-background border border-border text-xs"
              data-delegation-add={verb}
            >
              <option value="">+ {tt('pridať')}</option>
              {roles
                .filter((role) => role.key !== 'main_admin')
                .map((role) => {
                  const problem = targetProblem(edges, manager.key, role.key, roles)
                  const has = targets.includes(role.key)
                  return (
                    <option
                      key={role.key}
                      value={role.key}
                      disabled={problem !== null || has}
                      data-delegation-option={role.key}
                      data-delegation-option-problem={problem ?? ''}
                    >
                      {nameOf(role.key)}
                      {problem !== null ? ` - ${problemText(problem)}` : ''}
                    </option>
                  )
                })}
            </select>
          </div>
        )
      })}

      {passedOn.map((one) => (
        <p
          key={one.target}
          className="flex items-start gap-1.5 text-[11px] text-amber-500"
          data-delegation-passed-on={one.target}
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>{passedOnText(nameOf(manager.key), nameOf(one.target), one.rights.length)}</span>
        </p>
      ))}

      {ownProblems.map((one) => (
        <p
          key={`${one.edge.target}`}
          className="text-[11px] text-red-500"
          data-delegation-problem={`${one.edge.manager}>${one.edge.target}`}
        >
          {nameOf(one.edge.target)}: {one.problem === null ? '' : problemText(one.problem)}
        </p>
      ))}
    </div>
  )
}
